import React, { useState, useEffect, useRef } from 'react';
import styled from 'styled-components';
import { getAuth } from 'firebase/auth';
import { getAnalytics, logEvent } from 'firebase/analytics';
import { toast } from 'react-toastify';
import { Spinner } from '@styled-icons/fa-solid/Spinner';
import { API_URL } from '../../../constants/string-constants';
import DMConversationList from './DMConversationList';
import NotificationManager from '../../../services/NotificationManager';
import CreateGroupModal from './CreateGroupModal';
import AddMembersModal from './AddMembersModal';
import RenameGroupModal from './RenameGroupModal';
import FriendRequestsTab from './FriendRequestsTab';
import GroupsTab from './GroupsTab';
import DMChatView from './DMChatView';
import useDMSocketHandlers from './useDMSocketHandlers';
import { isUserBlocked, isBlockedBy } from '../../../utils/localstorage';

const SidebarOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  background-color: rgba(0, 0, 0, 0.5);
  z-index: 1000;
  opacity: ${(props) => (props.$isOpen ? 1 : 0)};
  visibility: ${(props) => (props.$isOpen ? 'visible' : 'hidden')};
  transition: opacity 0.3s ease, visibility 0.3s ease;
`;

const SidebarContainer = styled.div`
  width: 380px;
  position: fixed;
  top: 0;
  bottom: 0;
  left: 0;
  background-color: var(--clr-bg-secondary, #2a2a2a);
  border-right: 1px solid var(--clr-border, #444);
  display: flex;
  flex-direction: column;
  transform: translateX(${(props) => (props.$isOpen ? '0' : '-100%')});
  transition: transform 0.3s ease;

  /* iOS Safari safe area support */
  @supports (-webkit-touch-callout: none) {
    /* Account for safe areas */
    padding-top: env(safe-area-inset-top);
    padding-bottom: env(safe-area-inset-bottom);
  }

  @media (max-width: 768px) {
    width: 90vw;
    max-width: 380px;
  }
`;

const SidebarHeader = styled.div`
  padding: 16px;
  border-bottom: 1px solid var(--clr-border, #444);
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const SidebarTitle = styled.h3`
  color: var(--clr-text, #ffffff);
  margin: 0;
  font-size: 1.1rem;
  font-weight: 600;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  color: var(--clr-text-secondary, #aaa);
  cursor: pointer;
  font-size: 1.2rem;
  padding: 4px;
  border-radius: 4px;

  &:hover {
    background-color: var(--clr-bg-hover, #3a3a3a);
  }

  @media (min-width: 769px) {
    display: none;
  }
`;

const BackButton = styled.button`
  background: none;
  border: none;
  color: var(--clr-text-secondary, #aaa);
  cursor: pointer;
  font-size: 1.2rem;
  padding: 4px;
  border-radius: 4px;
  margin-right: 8px;

  &:hover {
    background-color: var(--clr-bg-hover, #3a3a3a);
  }
`;

const FriendInfo = styled.div`
  display: flex;
  align-items: center;
  flex: 1;
`;

const FriendName = styled.span`
  color: ${(props) => props.color || 'var(--clr-text, #ffffff)'};
  font-size: 1rem;
  font-weight: 600;
  margin-right: 8px;
`;

const OnlineStatus = styled.span`
  display: flex;
  align-items: center;
  font-size: 0.85rem;
  color: var(--clr-text-secondary, #aaa);
`;

const OnlineIndicator = styled.div`
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background-color: ${(props) =>
    props.isOnline ? '#4caf50' : 'var(--clr-text-disabled, #666)'};
  margin-right: 6px;
`;

const SidebarContent = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  min-height: 0;
  ${(props) =>
    props.mode === 'list'
      ? `
    overflow-y: auto;
    padding: 8px 0;
  `
      : `
    overflow: hidden;
    padding: 0;
  `}
`;

const ErrorContainer = styled.div`
  padding: 16px;
  color: var(--clr-error, #ff6b6b);
  text-align: center;
`;

const RetryButton = styled.button`
  background: var(--clr-primary, #007bff);
  color: white;
  border: none;
  padding: 8px 16px;
  border-radius: 4px;
  cursor: pointer;
  margin-top: 8px;

  &:hover {
    background: var(--clr-primary-hover, #0056b3);
  }
`;

const EmptyState = styled.div`
  padding: 32px 16px;
  text-align: center;
  color: var(--clr-text-secondary, #aaa);
`;

const EmptyTitle = styled.h4`
  margin: 0 0 8px 0;
  color: var(--clr-text, #ffffff);
`;

const EmptyText = styled.p`
  margin: 0;
  line-height: 1.4;
`;

const SearchContainer = styled.div`
  padding: 8px 16px;
  border-bottom: 1px solid var(--clr-border, #444);
`;

const SearchInput = styled.input`
  width: 100%;
  padding: 8px 12px;
  background-color: var(--clr-bg-tertiary, #333);
  border: 1px solid var(--clr-border, #444);
  border-radius: 6px;
  color: var(--clr-text, #ffffff);
  font-size: 14px;
  transition: all 0.2s ease;

  &:focus {
    outline: none;
    border-color: var(--clr-primary, #007bff);
    background-color: var(--clr-bg-secondary, #2a2a2a);
  }

  &::placeholder {
    color: var(--clr-text-secondary, #aaa);
  }
`;

const TabContainer = styled.div`
  display: flex;
  margin: 0 16px 0 16px;
  border-bottom: 1px solid var(--clr-border, #444);
`;

const Tab = styled.button`
  flex: 1;
  padding: 12px 16px;
  background: none;
  border: none;
  color: ${(props) =>
    props.$active
      ? 'var(--clr-primary, #007bff)'
      : 'var(--clr-text-secondary, #aaa)'};
  border-bottom: 2px solid
    ${(props) => (props.$active ? 'var(--clr-primary, #007bff)' : 'transparent')};
  cursor: pointer;
  font-size: 14px;
  font-weight: 500;
  transition: all 0.2s ease;
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;

  &:hover:not(:disabled) {
    color: var(--clr-primary, #007bff);
    background-color: var(--clr-bg-hover, #3a3a3a);
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.8;
  }
`;

const LoadingSpinner = styled(Spinner)`
  width: 14px;
  height: 14px;
  animation: spin 1s linear infinite;

  @keyframes spin {
    from {
      transform: rotate(0deg);
    }
    to {
      transform: rotate(360deg);
    }
  }
`;

/**
 * Message object structure for both DM and Group messages
 * @typedef {Object} Message
 * @property {string} id - Unique message ID
 * @property {string} messageText - The message content
 * @property {string} createdAt - ISO timestamp
 * @property {string} color - Hex color code for the message
 * @property {string|null} font - Font styling
 * @property {string|null} effect - Message effect
 * @property {string[]} readBy - Array of user IDs who have read the message
 * @property {boolean} isFromMe - Whether the message is from the current user
 * @property {boolean} isSystemMessage - Whether this is a system message
 * @property {Object|undefined} sender - Sender information (for group messages)
 * @property {string} sender.id - Sender's user ID
 * @property {string} sender.nickname - Sender's display name
 * @property {string|null} sender.custom_color - Custom color set by sender
 * @property {string|null} sender.assigned_color - System-assigned color
 *
 * Note: DM messages may not have a 'sender' object, using isFromMe instead
 * System messages may have different structure
 */

function DMSidebar({
  isOpen,
  onClose,
  selectedConversation,
  onConversationSelect,
  socket,
  onUnreadCountChange,
  onViewProfile,
}) {
  const [conversations, setConversations] = useState([]);
  const [friends, setFriends] = useState([]);
  const [friendRequests, setFriendRequests] = useState([]);
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentMode, setCurrentMode] = useState('list'); // 'list' or 'chat'
  const [activeTab, setActiveTab] = useState('friends'); // 'friends', 'requests', or 'groups'
  const [activeChatConversation, setActiveChatConversation] = useState(null);
  const [activeGroupConversation, setActiveGroupConversation] = useState(null);
  const [chatMessages, setChatMessages] = useState([]);
  const [chatType, setChatType] = useState('dm'); // 'dm' or 'group'
  const [loadingMessages, setLoadingMessages] = useState(false);
  const [processing, setProcessing] = useState(new Set());
  const [confirmRemove, setConfirmRemove] = useState(null);
  const [confirmLeaveGroup, setConfirmLeaveGroup] = useState(null);
  const [sortByLastActive, setSortByLastActive] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [showCreateGroupModal, setShowCreateGroupModal] = useState(false);
  const [showAddMembersModal, setShowAddMembersModal] = useState(null); // Store groupId when showing modal
  const [showRenameGroupModal, setShowRenameGroupModal] = useState(null); // Store group object when showing modal

  // Use socket handlers hook
  useDMSocketHandlers({
    socket,
    currentMode,
    activeChatConversation,
    activeGroupConversation,
    chatType,
    setChatMessages,
    setFriendRequests,
    setGroups,
    setActiveGroupConversation,
  });

  // Subscribe to notification counts from NotificationManager
  const [notificationCounts, setNotificationCounts] = useState({
    dm: 0,
    friendRequests: 0,
    groups: 0,
    total: 0,
  });

  // Active conversation context for notification filtering
  const [activeConversationContext, setActiveConversationContext] = useState({
    type: 'none', // 'dm', 'group', or 'none'
    id: null, // conversationId or groupId
    isVisible: false, // whether DM sidebar is open and showing this conversation
  });

  // Subscribe to NotificationManager for real-time count updates
  useEffect(() => {
    const unsubscribe = NotificationManager.subscribe((counts) => {
      setNotificationCounts(counts);
    });

    return () => {
      unsubscribe();
    };
  }, []);

  // Update NotificationManager with conversation counts when they change
  useEffect(() => {
    const conversationCounts = {};
    conversations.forEach((conv) => {
      if (conv.id && conv.unreadCount > 0) {
        conversationCounts[conv.id] = conv.unreadCount;
      }
    });
    NotificationManager.setConversationCounts(conversationCounts);
  }, [conversations]);

  // Update NotificationManager with group counts when they change
  useEffect(() => {
    // Hydrate the mute set first so the aggregate recalculation inside
    // setGroupCounts already excludes muted groups.
    const mutedIds = groups.filter((g) => g && g.muted).map((g) => g.id);
    NotificationManager.setMutedGroupIds(mutedIds);

    const groupCounts = {};
    groups.forEach((group) => {
      if (group.id && group.unreadCount > 0) {
        groupCounts[group.id] = group.unreadCount;
      }
    });
    NotificationManager.setGroupCounts(groupCounts);
  }, [groups]);

  // Update NotificationManager with friend request count
  useEffect(() => {
    NotificationManager.setFriendRequestCount(friendRequests.length);
  }, [friendRequests]);

  // Update total unread count whenever NotificationManager counts change (sent to parent for hamburger button badge)
  useEffect(() => {
    if (onUnreadCountChange) {
      onUnreadCountChange(notificationCounts.total);
    }
  }, [notificationCounts, onUnreadCountChange]);

  useEffect(() => {
    if (isOpen) {
      // Add a small delay to ensure auth is ready
      const timer = setTimeout(() => {
        if (activeTab === 'friends') {
          loadConversationsAndFriends();
        } else if (activeTab === 'requests') {
          loadFriendRequests();
        } else if (activeTab === 'groups') {
          loadGroups();
        }
      }, 200);

      return () => clearTimeout(timer);
    }
  }, [isOpen, activeTab]);

  // Additional effect to reload groups when switching back from chat mode to list mode with groups tab active
  useEffect(() => {
    if (currentMode === 'list' && activeTab === 'groups' && isOpen) {
      // Small delay to ensure the mode transition is complete
      const timer = setTimeout(() => {
        loadGroups();
      }, 100);

      return () => clearTimeout(timer);
    }
  }, [currentMode, activeTab, isOpen]);

  // Update active conversation context visibility when sidebar opens/closes
  useEffect(() => {
    const newIsVisible =
      isOpen &&
      currentMode === 'chat' &&
      activeConversationContext.type !== 'none';
    const wasInvisible = !activeConversationContext.isVisible;
    const becomingVisible = newIsVisible && wasInvisible;

    // Update NotificationManager with visibility change
    if (
      activeConversationContext.type !== 'none' &&
      activeConversationContext.id
    ) {
      NotificationManager.setActiveConversation(
        activeConversationContext.type,
        activeConversationContext.id,
        newIsVisible
      );
    }

    setActiveConversationContext((prev) => ({
      ...prev,
      isVisible: newIsVisible,
    }));

    // If sidebar reopens with an active conversation, mark messages as read
    if (
      becomingVisible &&
      activeConversationContext.type === 'group' &&
      activeGroupConversation
    ) {
      markGroupAsRead(activeGroupConversation.id);
    } else if (
      becomingVisible &&
      activeConversationContext.type === 'dm' &&
      activeChatConversation
    ) {
      markDMAsRead(activeChatConversation.id);
    }
  }, [
    isOpen,
    currentMode,
    activeConversationContext.type,
    activeConversationContext.isVisible,
    activeGroupConversation,
    activeChatConversation,
  ]);

  // Active conversation context is now managed by NotificationManager

  // Helper function to mark group messages as read
  const markGroupAsRead = async (groupId) => {
    try {
      const auth = getAuth();
      if (!auth.currentUser) return;

      const token = await auth.currentUser.getIdToken();
      const response = await fetch(API_URL + `groups/${groupId}/read`, {
        method: 'PUT',
        headers: {
          Authorization: 'Bearer ' + token,
        },
      });

      if (response.ok) {
        // Update local groups state to reflect that messages are now read
        setGroups((prev) => {
          const updated = prev.map((group) =>
            group.id === groupId ? { ...group, unreadCount: 0 } : group
          );
          return updated;
        });

        // NotificationManager will handle the count update
      } else {
        console.error('Failed to mark group as read:', response.status);
      }
    } catch (error) {
      console.error('Error marking group as read:', error);
    }
  };

  // Helper function to mark DM messages as read
  const markDMAsRead = async (conversationId) => {
    try {
      const auth = getAuth();
      if (!auth.currentUser) return;

      const token = await auth.currentUser.getIdToken();
      const response = await fetch(
        API_URL + `direct-messages/${conversationId}/read`,
        {
          method: 'PUT',
          headers: {
            Authorization: 'Bearer ' + token,
          },
        }
      );

      if (response.ok) {
        // Update local conversations state to reflect that messages are now read
        setConversations((prev) =>
          prev.map((conv) =>
            conv.id === conversationId ? { ...conv, unreadCount: 0 } : conv
          )
        );

        // NotificationManager will handle the count update
      }
    } catch (error) {
      console.error('Error marking conversation as read:', error);
    }
  };

  // Also reload when socket becomes available
  useEffect(() => {
    if (isOpen && socket && conversations.length === 0 && !loading) {
      loadConversationsAndFriends();
    }
  }, [socket, isOpen]);

  // Listen for new DM messages to update conversation list
  useEffect(() => {
    if (!socket) return;

    const handleDMMessage = (message) => {
      updateConversationWithNewMessage(message);
    };

    socket.on('dm_message', handleDMMessage);

    return () => {
      socket.off('dm_message', handleDMMessage);
    };
  }, [socket]);

  const loadConversationsAndFriends = async () => {
    try {
      setLoading(true);
      setError(null);

      const auth = getAuth();

      // Wait for auth to be ready if needed
      let user = auth.currentUser;
      if (!user) {
        // Wait a bit more for auth to be ready
        await new Promise((resolve) => setTimeout(resolve, 500));
        user = auth.currentUser;
      }

      if (!user) {
        setError('Please sign in to view conversations');
        return;
      }
      const token = await user.getIdToken();

      // Load friends with DM info
      const friendsResponse = await fetch(
        API_URL + 'friends/dm-conversations',
        {
          headers: {
            Authorization: 'Bearer ' + token,
          },
        }
      );

      if (friendsResponse.ok) {
        const friendsData = await friendsResponse.json();
        setFriends(friendsData.friends || []);

        // Extract conversations that have messages
        const activeConversations = friendsData.friends
          .filter(
            (friend) => friend.dm.hasConversation && friend.dm.latestMessage
          )
          .map((friend) => ({
            id: friend.dm.conversationId,
            friend: {
              id: friend.id,
              nickname: friend.nickname,
              custom_color: friend.custom_color,
              assigned_color: friend.assigned_color,
              last_active_at: friend.last_active_at,
              isOnline: friend.isOnline,
            },
            lastMessage: friend.dm.latestMessage,
            unreadCount: friend.dm.unreadCount,
            lastMessageAt: friend.dm.lastMessageAt,
          }))
          .sort(
            (a, b) => new Date(b.lastMessageAt) - new Date(a.lastMessageAt)
          );

        setConversations(activeConversations);

        // Conversation counts will be updated via the useEffect that watches conversations
      } else {
        const errorData = await friendsResponse.json();

        if (errorData.requireAccount) {
          setError('You need an account number to use direct messages');
        } else {
          setError(errorData.error || 'Failed to load conversations');
        }
      }
    } catch (error) {
      console.error('Error loading conversations:', error);
      setError('Failed to load conversations. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const updateConversationWithNewMessage = (message) => {
    setConversations((prevConversations) => {
      const existingIndex = prevConversations.findIndex(
        (conv) => conv.id === message.conversationId
      );

      const updatedConversation = {
        id: message.conversationId,
        lastMessage: {
          id: message.id,
          messageText: message.messageText,
          createdAt: new Date(message.createdAt),
          isFromMe: message.userId === 'self',
        },
        unreadCount:
          message.userId === 'self'
            ? 0
            : existingIndex >= 0
            ? prevConversations[existingIndex].unreadCount + 1
            : 1,
        lastMessageAt: new Date(message.createdAt),
      };

      if (existingIndex >= 0) {
        // Update existing conversation
        const updated = [...prevConversations];
        updated[existingIndex] = {
          ...updated[existingIndex],
          ...updatedConversation,
        };

        // Move to top
        const conversation = updated.splice(existingIndex, 1)[0];
        return [conversation, ...updated];
      } else {
        // New conversation - find friend info
        const friend = friends.find(
          (f) => f.dm.conversationId === message.conversationId
        );
        if (friend) {
          const newConversation = {
            ...updatedConversation,
            friend: {
              id: friend.id,
              nickname: friend.nickname,
              custom_color: friend.custom_color,
              assigned_color: friend.assigned_color,
              last_active_at: friend.last_active_at,
              isOnline: friend.isOnline,
            },
          };
          return [newConversation, ...prevConversations];
        }
      }

      return prevConversations;
    });
  };

  const loadConversationMessages = async (conversationId) => {
    try {
      setLoadingMessages(true);
      setChatMessages([]);

      const auth = getAuth();
      if (!auth.currentUser) {
        toast.error('Please sign in to view messages');
        return;
      }

      const token = await auth.currentUser.getIdToken();
      const response = await fetch(
        API_URL + `direct-messages/${conversationId}?limit=30`,
        {
          headers: {
            Authorization: 'Bearer ' + token,
          },
        }
      );

      if (response.ok) {
        const data = await response.json();
        const sorted = (data.messages || [])
          .slice()
          .sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt));
        setChatMessages(sorted);

        // Mark unread messages as read (similar to DMChatView)
        const unreadMessages = data.messages.filter(
          (msg) => !msg.isFromMe && !msg.readAt
        );
        if (unreadMessages.length > 0) {
          // Mark conversation as read on backend
          try {
            const markAsReadResponse = await fetch(
              API_URL + `direct-messages/${conversationId}/read`,
              {
                method: 'PUT',
                headers: {
                  Authorization: 'Bearer ' + token,
                },
              }
            );

            if (markAsReadResponse.ok) {
              // Update the local conversations state to reflect that messages are now read
              setConversations((prev) =>
                prev.map((conv) =>
                  conv.id === conversationId
                    ? { ...conv, unreadCount: 0 }
                    : conv
                )
              );
            }
          } catch (error) {
            console.error('Error marking conversation as read:', error);
          }
        }
      } else {
        const errorData = await response.json();
        setError(errorData.error || 'Failed to load messages');
      }
    } catch (error) {
      console.error('Error loading conversation messages:', error);
      setError('Failed to load messages. Please try again.');
    } finally {
      setLoadingMessages(false);
    }
  };

  const sendMessage = (messageText) => {
    if (!socket || !messageText.trim()) return;

    const analytics = getAnalytics();

    if (chatType === 'dm' && activeChatConversation) {
      socket.emit('dm_message', {
        conversationId: activeChatConversation.id,
        messageText: messageText.trim(),
      });
      logEvent(analytics, 'sent_message', {
        messageLength: messageText.trim().length,
        messageType: 'private',
      });
    } else if (chatType === 'group' && activeGroupConversation) {
      socket.emit('group_message', {
        groupId: activeGroupConversation.id,
        messageText: messageText.trim(),
      });
      logEvent(analytics, 'sent_message', {
        messageLength: messageText.trim().length,
        messageType: 'group',
      });
    }
  };

  const handleBackToList = () => {
    setCurrentMode('list');
    setActiveChatConversation(null);
    setActiveGroupConversation(null);
    setChatMessages([]);

    // Clear active conversation context using NotificationManager
    NotificationManager.setActiveConversation('none', null, false);

    // Also update local state for backwards compatibility
    setActiveConversationContext({
      type: 'none',
      id: null,
      isVisible: false,
    });

    // Leave the conversation room based on chat type
    if (socket) {
      if (chatType === 'dm' && activeChatConversation) {
        socket.emit('leave_dm_conversation', activeChatConversation.id);
      } else if (chatType === 'group' && activeGroupConversation) {
        socket.emit('leave_group_conversation', activeGroupConversation.id);
      }
    }

    // Reload conversations to refresh unread counts after returning from chat
    if (chatType === 'dm') {
      loadConversationsAndFriends();
    } else if (chatType === 'group') {
      loadGroups();
    }

    setChatType('dm'); // Reset to default
  };


  const startNewConversation = async (friend) => {
    // Add null safety check
    if (!friend) {
      console.error('Cannot start conversation: friend is null');
      toast.error('Unable to start conversation - friend data is missing');
      return;
    }

    try {
      const auth = getAuth();
      if (!auth.currentUser) {
        toast.error('Please sign in to start conversations');
        return;
      }

      const token = await auth.currentUser.getIdToken();
      const response = await fetch(API_URL + 'direct-messages/start', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: 'Bearer ' + token,
        },
        body: JSON.stringify({
          friendId: friend.id,
        }),
      });

      if (response.ok) {
        const data = await response.json();

        // Create new conversation object
        const newConversation = {
          id: data.conversationId,
          friend: data.friend,
          lastMessage: null,
          unreadCount: 0,
          lastMessageAt: new Date(),
        };

        // Add to conversations list if not already there
        setConversations((prev) => {
          const exists = prev.find((conv) => conv.id === data.conversationId);
          if (!exists) {
            return [newConversation, ...prev];
          }
          return prev;
        });

        // Switch to chat mode with this new conversation
        handleConversationSelect(newConversation);
      } else {
        const errorData = await response.json();
        toast.error(errorData.error || 'Failed to start conversation');
      }
    } catch (error) {
      console.error('Error starting conversation:', error);
      toast.error('Failed to start conversation');
    }
  };

  const handleConversationSelect = async (conversation) => {
    // Switch to chat mode with this conversation
    setCurrentMode('chat');
    setActiveChatConversation(conversation);

    // Update active conversation context using NotificationManager
    NotificationManager.setActiveConversation('dm', conversation.id, true);

    // Also update local state for backwards compatibility
    setActiveConversationContext({
      type: 'dm',
      id: conversation.id,
      isVisible: true,
    });

    // Marking as read will be handled in loadConversationMessages

    // Load messages for this conversation
    await loadConversationMessages(conversation.id);

    // Join the conversation room
    if (socket) {
      socket.emit('join_dm_conversation', conversation.id);
    }
  };

  const loadFriendRequests = async () => {
    try {
      setLoading(true);
      setError(null);
      const auth = getAuth();

      if (!auth.currentUser) {
        setError('Please sign in to view friend requests');
        return;
      }

      const token = await auth.currentUser.getIdToken();
      const response = await fetch(API_URL + 'friends/requests', {
        headers: {
          Authorization: 'Bearer ' + token,
        },
      });

      if (response.ok) {
        const data = await response.json();
        const requests = data.requests || [];
        setFriendRequests(requests);
        // Friend request count will be updated via NotificationManager
      } else {
        const errorData = await response.json();
        setError(errorData.error || 'Failed to fetch friend requests');
      }
    } catch (error) {
      console.error('Error fetching friend requests:', error);
      setError('Failed to fetch friend requests');
    } finally {
      setLoading(false);
    }
  };

  const loadGroups = async () => {
    try {
      setLoading(true);
      setError(null);
      const auth = getAuth();

      if (!auth.currentUser) {
        setError('Please sign in to view groups');
        return;
      }

      const token = await auth.currentUser.getIdToken();
      const response = await fetch(API_URL + 'groups', {
        headers: {
          Authorization: 'Bearer ' + token,
        },
      });

      if (response.ok) {
        const data = await response.json();
        const groups = data.groups || [];
        setGroups(groups);

        // Group counts will be updated via the useEffect that watches groups
      } else {
        const errorData = await response.json();
        setError(errorData.error || 'Failed to fetch groups');
      }
    } catch (error) {
      console.error('Error fetching groups:', error);
      setError('Failed to fetch groups');
    } finally {
      setLoading(false);
    }
  };

  const handleRemoveFriend = async (friendId) => {
    try {
      setProcessing((prev) => new Set(prev).add(friendId));
      setConfirmRemove(null);
      const auth = getAuth();

      if (!auth.currentUser) {
        setError('Please sign in to remove friends');
        return;
      }

      const token = await auth.currentUser.getIdToken();
      const response = await fetch(API_URL + `friends/remove/${friendId}`, {
        method: 'DELETE',
        headers: {
          Authorization: 'Bearer ' + token,
        },
      });

      if (response.ok) {
        const analytics = getAnalytics();
        logEvent(analytics, 'friend_removed');

        toast.success('Friend removed successfully!');

        // Remove the friend from the list and reload data
        setFriends((prev) =>
          prev.filter((friend) => friend && friend.id !== friendId)
        );
        setConversations((prev) =>
          prev.filter((conv) => conv.friend && conv.friend.id !== friendId)
        );
      } else {
        const errorData = await response.json();
        setError(errorData.error || 'Failed to remove friend');
      }
    } catch (error) {
      console.error('Error removing friend:', error);
      setError('Failed to remove friend');
    } finally {
      setProcessing((prev) => {
        const newSet = new Set(prev);
        newSet.delete(friendId);
        return newSet;
      });
    }
  };

  const handleCreateGroup = async (groupData) => {
    try {
      const auth = getAuth();
      if (!auth.currentUser) {
        setError('Please sign in to create groups');
        return;
      }

      const token = await auth.currentUser.getIdToken();
      const response = await fetch(API_URL + 'groups', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: 'Bearer ' + token,
        },
        body: JSON.stringify(groupData),
      });

      if (response.ok) {
        const newGroup = await response.json();
        setGroups((prev) => [newGroup, ...prev]);
        setShowCreateGroupModal(false);
        toast.success('Group created successfully!');

        const analytics = getAnalytics();
        logEvent(analytics, 'group_created');
      } else {
        const errorData = await response.json();
        toast.error(errorData.error || 'Failed to create group');
      }
    } catch (error) {
      console.error('Error creating group:', error);
      toast.error('Failed to create group');
    }
  };

  const handleAddMembers = async (groupId, memberIds) => {
    try {
      const auth = getAuth();
      if (!auth.currentUser) {
        toast.error('Please sign in to add members');
        return;
      }

      const token = await auth.currentUser.getIdToken();
      const response = await fetch(API_URL + `groups/${groupId}/members`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: 'Bearer ' + token,
        },
        body: JSON.stringify({ memberIds }),
      });

      if (response.ok) {
        const result = await response.json();
        const analytics = getAnalytics();
        logEvent(analytics, 'group_members_added', {
          memberCount: memberIds.length,
        });

        // Update the group in our local state with new members
        setGroups((prev) =>
          prev.map((group) =>
            group.id === groupId
              ? {
                  ...group,
                  members: result.group.members,
                  member_count: result.group.member_count,
                }
              : group
          )
        );

        toast.success(
          `Added ${memberIds.length} member${
            memberIds.length > 1 ? 's' : ''
          } to the group`
        );
        setShowAddMembersModal(null);
      } else {
        const errorData = await response.json();
        toast.error(errorData.error || 'Failed to add members');
      }
    } catch (error) {
      console.error('Error adding members:', error);
      toast.error('Failed to add members');
    }
  };

  const handleRenameGroup = async (groupId, newName) => {
    try {
      const auth = getAuth();
      if (!auth.currentUser) {
        toast.error('Please sign in to rename groups');
        return;
      }

      const token = await auth.currentUser.getIdToken();
      const response = await fetch(API_URL + `groups/${groupId}/rename`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: 'Bearer ' + token,
        },
        body: JSON.stringify({ name: newName }),
      });

      if (response.ok) {
        const result = await response.json();
        const analytics = getAnalytics();
        logEvent(analytics, 'group_renamed');

        // Update the group in our local state
        setGroups((prev) =>
          prev.map((group) =>
            group.id === groupId ? { ...group, name: result.group.name } : group
          )
        );

        // Update active group if it's being viewed
        if (activeGroupConversation?.id === groupId) {
          setActiveGroupConversation((prev) => ({
            ...prev,
            name: result.group.name,
          }));
        }

        toast.success('Group renamed successfully');
        setShowRenameGroupModal(null);
      } else {
        const errorData = await response.json();
        toast.error(errorData.error || 'Failed to rename group');
      }
    } catch (error) {
      console.error('Error renaming group:', error);
      toast.error('Failed to rename group');
    }
  };

  const handleGroupSelect = async (group) => {
    try {
      setActiveGroupConversation(group);
      setActiveChatConversation(null); // Clear any active DM
      setChatType('group');
      setCurrentMode('chat');
      setChatMessages([]);
      setLoadingMessages(true);

      // Update active conversation context using NotificationManager
      NotificationManager.setActiveConversation('group', group.id, true);

      // Also update local state for backwards compatibility
      setActiveConversationContext({
        type: 'group',
        id: group.id,
        isVisible: true,
      });

      // Load group messages
      const auth = getAuth();
      if (!auth.currentUser) {
        setError('Please sign in to view group messages');
        return;
      }

      const token = await auth.currentUser.getIdToken();
      const response = await fetch(API_URL + `groups/${group.id}/messages`, {
        headers: {
          Authorization: 'Bearer ' + token,
        },
      });

      if (response.ok) {
        const data = await response.json();
        const messages = data.messages || [];

        // Ensure messages are valid and filter out blocked users (matches RoomV2 behavior)
        const validMessages = messages.filter(
          (msg) =>
            msg &&
            typeof msg === 'object' &&
            !isUserBlocked(msg.sender?.id) &&
            !isBlockedBy(msg.sender?.id)
        );
        setChatMessages(validMessages);

        // Mark unread messages as read (similar to DM logic)
        const unreadMessages = validMessages.filter(
          (msg) => msg && !msg.isFromMe && !msg.readAt
        );

        if (unreadMessages.length > 0 || group.unreadCount > 0) {
          // Mark group messages as read on backend
          try {
            const markAsReadResponse = await fetch(
              API_URL + `groups/${group.id}/read`,
              {
                method: 'PUT',
                headers: {
                  Authorization: 'Bearer ' + token,
                },
              }
            );

            if (markAsReadResponse.ok) {
              // Update the local groups state to reflect that messages are now read
              setGroups((prev) => {
                const updated = prev.map((g) =>
                  g.id === group.id ? { ...g, unreadCount: 0 } : g
                );
                return updated;
              });
            } else {
              const errorText = await markAsReadResponse.text();
              console.error(
                'Failed to mark group as read:',
                markAsReadResponse.status,
                errorText
              );
            }
          } catch (error) {
            console.error('Error marking group messages as read:', error);
          }
        }

        // Join the group conversation via websocket
        if (socket) {
          socket.emit('join_group_conversation', group.id);
        }
      } else {
        const errorData = await response.json();
        setError(errorData.error || 'Failed to load group messages');
      }
    } catch (error) {
      console.error('Error loading group messages:', error);
      setError('Failed to load group messages');
    } finally {
      setLoadingMessages(false);
    }
  };

  // Filter friends and conversations based on search term
  const filterItems = (items, searchTerm) => {
    if (!searchTerm.trim()) return items;

    const lowerSearchTerm = searchTerm.toLowerCase();
    return items.filter((item) => {
      // For conversations, check friend nickname
      if (item.friend) {
        return (item.friend.nickname || '')
          .toLowerCase()
          .includes(lowerSearchTerm);
      }
      // For friends, check nickname
      return (item.nickname || '').toLowerCase().includes(lowerSearchTerm);
    });
  };

  const filteredConversations = filterItems(conversations, searchTerm);
  const filteredFriends = filterItems(friends, searchTerm);

  return (
    <SidebarOverlay $isOpen={isOpen} onClick={onClose}>
      <SidebarContainer $isOpen={isOpen} onClick={(e) => e.stopPropagation()}>
        <SidebarHeader>
          {currentMode === 'chat' &&
          (activeChatConversation || activeGroupConversation) ? (
            <>
              <BackButton onClick={handleBackToList}>←</BackButton>
              <FriendInfo>
                <FriendName
                  color={
                    chatType === 'dm'
                      ? activeChatConversation.friend?.custom_color ||
                        activeChatConversation.friend?.assigned_color
                      : '#999999'
                  }
                >
                  {chatType === 'dm'
                    ? activeChatConversation.friend?.nickname || 'Anon'
                    : activeGroupConversation?.name || 'Group Chat'}
                </FriendName>
                <OnlineStatus>
                  {chatType === 'dm' ? (
                    <>
                      <OnlineIndicator
                        isOnline={activeChatConversation.friend?.isOnline}
                      />
                      {activeChatConversation.friend?.isOnline
                        ? 'Online'
                        : 'Offline'}
                    </>
                  ) : (
                    `${activeGroupConversation?.member_count || 0} members`
                  )}
                </OnlineStatus>
              </FriendInfo>
            </>
          ) : (
            <SidebarTitle>Friends & Messages</SidebarTitle>
          )}
          <CloseButton onClick={onClose}>×</CloseButton>
        </SidebarHeader>

        {currentMode === 'list' && (
          <>
            <TabContainer>
              <Tab
                $active={activeTab === 'friends'}
                onClick={() => setActiveTab('friends')}
                disabled={loading}
              >
                {loading && activeTab === 'friends' && <LoadingSpinner />}
                <span>
                  Friends{' '}
                  {notificationCounts.dm > 0 &&
                    `(${notificationCounts.dm} unread)`}
                </span>
              </Tab>
              <Tab
                $active={activeTab === 'requests'}
                onClick={() => setActiveTab('requests')}
                disabled={loading}
              >
                {loading && activeTab === 'requests' && <LoadingSpinner />}
                <span>
                  Requests{' '}
                  {notificationCounts.friendRequests > 0 &&
                    `(${notificationCounts.friendRequests} new)`}
                </span>
              </Tab>
              <Tab
                $active={activeTab === 'groups'}
                onClick={() => setActiveTab('groups')}
                disabled={loading}
              >
                {loading && activeTab === 'groups' && <LoadingSpinner />}
                <span>
                  Groups{' '}
                  {notificationCounts.groups > 0 &&
                    `(${notificationCounts.groups} unread)`}
                </span>
              </Tab>
            </TabContainer>

            {activeTab === 'friends' && (
              <SearchContainer>
                <SearchInput
                  type='text'
                  placeholder='Search friends...'
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </SearchContainer>
            )}
          </>
        )}

        <SidebarContent mode={currentMode}>
          {currentMode === 'list' ? (
            // List Mode with Tabs
            <>
              {error && !loading && (
                <ErrorContainer>
                  {error}
                  <br />
                  <RetryButton
                    onClick={
                      activeTab === 'friends'
                        ? loadConversationsAndFriends
                        : activeTab === 'requests'
                        ? loadFriendRequests
                        : loadGroups
                    }
                  >
                    Retry
                  </RetryButton>
                </ErrorContainer>
              )}

              {!error && activeTab === 'friends' && (
                <>
                  {conversations.length === 0 &&
                    friends.length === 0 &&
                    !searchTerm && (
                      <EmptyState>
                        <EmptyTitle>No friends yet</EmptyTitle>
                        <EmptyText>
                          Add friends to start sending direct messages!
                        </EmptyText>
                      </EmptyState>
                    )}

                  {searchTerm &&
                    filteredConversations.length === 0 &&
                    filteredFriends.length === 0 && (
                      <EmptyState>
                        <EmptyTitle>No friends found</EmptyTitle>
                        <EmptyText>No friends match "{searchTerm}"</EmptyText>
                      </EmptyState>
                    )}

                  <DMConversationList
                    conversations={filteredConversations}
                    friends={filteredFriends}
                    selectedConversation={activeChatConversation}
                    onConversationSelect={handleConversationSelect}
                    onStartConversation={startNewConversation}
                    onRemoveFriend={(friend) => handleRemoveFriend(friend.id)}
                    onViewProfile={onViewProfile}
                    sortByLastActive={sortByLastActive}
                    onSortToggle={() => setSortByLastActive(!sortByLastActive)}
                  />
                </>
              )}

              {!error && activeTab === 'requests' && (
                <FriendRequestsTab
                  friendRequests={friendRequests}
                  processing={processing}
                  onRequestAction={(requestId, action, isProcessing) => {
                    if (isProcessing) {
                      setProcessing((prev) => new Set(prev).add(requestId));
                    } else {
                      setProcessing((prev) => {
                        const newSet = new Set(prev);
                        newSet.delete(requestId);
                        return newSet;
                      });

                      if (action !== 'processing') {
                        // Remove the processed request from the list
                        setFriendRequests((prev) =>
                          prev.filter((req) => req.id !== requestId)
                        );
                      }
                    }
                  }}
                  loading={loading}
                />
              )}

              {!error && activeTab === 'groups' && (
                <GroupsTab
                  groups={groups}
                  processing={processing}
                  onGroupSelect={handleGroupSelect}
                  onShowCreateModal={() => setShowCreateGroupModal(true)}
                  onShowRenameModal={setShowRenameGroupModal}
                  onShowAddMembersModal={setShowAddMembersModal}
                  onLeaveGroup={(groupId) => {
                    // Remove the group from the list
                    setGroups((prev) =>
                      prev.filter((group) => group.id !== groupId)
                    );

                    // If we were viewing this group, go back to list
                    if (activeGroupConversation?.id === groupId) {
                      handleBackToList();
                    }
                  }}
                  onGroupMuteToggled={(groupId, muted) => {
                    // Flip the muted flag on the local groups array; the
                    // useEffect that watches `groups` will re-sync the mute
                    // set into NotificationManager. unreadCount is preserved
                    // so unmuting immediately restores the real badge.
                    setGroups((prev) =>
                      prev.map((group) =>
                        group.id === groupId ? { ...group, muted } : group
                      )
                    );
                  }}
                  loading={loading}
                />
              )}
            </>
          ) : (
            // Chat Mode
            <DMChatView
              chatMessages={chatMessages}
              loadingMessages={loadingMessages}
              chatType={chatType}
              activeChatConversation={activeChatConversation}
              activeGroupConversation={activeGroupConversation}
              onSendMessage={sendMessage}
            />
          )}
        </SidebarContent>
      </SidebarContainer>
      
      {showCreateGroupModal && (
        <CreateGroupModal
          onClose={() => setShowCreateGroupModal(false)}
          friends={friends}
          onCreateGroup={handleCreateGroup}
        />
      )}

      {showAddMembersModal && (
        <AddMembersModal
          onClose={() => setShowAddMembersModal(null)}
          friends={friends}
          currentGroup={groups.find((g) => g.id === showAddMembersModal)}
          onAddMembers={(memberIds) =>
            handleAddMembers(showAddMembersModal, memberIds)
          }
        />
      )}

      {showRenameGroupModal && (
        <RenameGroupModal
          onClose={() => setShowRenameGroupModal(null)}
          group={showRenameGroupModal}
          onRename={(newName) =>
            handleRenameGroup(showRenameGroupModal.id, newName)
          }
        />
      )}
    </SidebarOverlay>
  );
}

export default DMSidebar;
